import { FunctionComponent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus, faTag } from '@fortawesome/pro-solid-svg-icons';
import { InventoryMovement } from '@spike/product-model';
import { Spinner } from 'components/UI';
import moment from 'moment';
import clsx from 'clsx';
import styles from './StockSidebar.module.scss';

interface StockHistoryProps {
    movements: Array<InventoryMovement>;
    loading?: boolean;
    locationName?: string;
    onViewDetails: (movement: InventoryMovement) => void;
}

const formatMovementDate = (movement: InventoryMovement): string => {
    if (!movement.createdAt || !movement.createdAt.isValid()) {
        return '-';
    }

    return movement.createdAt.format('MMM D, YYYY · h:mma').replace('AM', 'am').replace('PM', 'pm');
};

const formatDelta = (delta: number): string => {
    if (delta > 0) {
        return `+${delta}`;
    }

    return String(delta);
};

export const getMovementReason = (movement: InventoryMovement): string => {
    const reason = movement.reason?.trim() ?? '';
    const invoiceNumber = movement.invoice?.number;

    if (!invoiceNumber) {
        return reason;
    }

    const withInvoiceNumber = reason.replace(/order\s*#?:?\s*\d+/gi, `Invoice #${invoiceNumber}`);

    if (withInvoiceNumber !== reason) {
        return withInvoiceNumber;
    }

    return reason || `Invoice #${invoiceNumber}`;
};

const getMovementTitle = (movement: InventoryMovement): string => {
    const reason = getMovementReason(movement) || (movement.quantity.delta >= 0 ? 'Add Stock' : 'Reduce Stock');
    const staffName = movement.staff?.name || 'Staff Name';

    return `${reason} (${formatDelta(movement.quantity.delta)}) by ${staffName}`;
};

export const StockHistory: FunctionComponent<StockHistoryProps> = ({
    movements,
    loading,
    locationName,
    onViewDetails
}) => {
    if (loading) {
        return (
            <div className={styles.spinnerWrap}>
                <Spinner />
            </div>
        );
    }

    return (
        <div>
            <h3 className={styles.historySectionTitle}>Stock History</h3>
            {movements.length === 0 ? (
                <div className={styles.emptyHistory}>
                    <div className={styles.emptyIconWrap}>
                        <FontAwesomeIcon icon={faTag} />
                    </div>
                    <p className={styles.emptyTitle}>No stock history yet</p>
                </div>
            ) : (
                <div className={styles.historyList}>
                    {movements.map(movement => {
                        const isAdd = movement.quantity.delta > 0;

                        return (
                            <div
                                key={movement.uuid || movement.id}
                                className={styles.historyItem}
                            >
                                <div
                                    className={clsx(
                                        styles.historyIcon,
                                        isAdd ? styles.historyIconAdd : styles.historyIconReduce
                                    )}
                                >
                                    <FontAwesomeIcon icon={isAdd ? faPlus : faMinus} />
                                </div>
                                <div className={styles.historyContent}>
                                    <span className={styles.historyDate}>{formatMovementDate(movement)}</span>
                                    <span className={styles.historyTitle}>{getMovementTitle(movement)}</span>
                                    <span className={styles.historyLocation}>{locationName || '-'}</span>
                                </div>
                                <button
                                    type="button"
                                    className={styles.viewDetails}
                                    onClick={() => onViewDetails(movement)}
                                >
                                    View Details
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default StockHistory;
